import { marketingBackendApiUrl } from '../config';
import {
  parseMarketingApiJsonBody,
  WAITLIST_UNAVAILABLE_MESSAGE,
} from '../utils/upstreamHttpResponse';

export type PartnerVenueApplication = {
  name: string;
  city: string;
  state?: string;
  /** Canonical VenueCategory slugs; the server drops any it does not recognise. */
  categories?: string[];
  address?: string;
  websiteUrl?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  description?: string;
  applicantNote?: string;
  offer?: string;
  promoCode?: string;
  /**
   * Honeypot. The form renders this input off-screen with no label and autocomplete off, so a
   * person never fills it and a bot filling every field does. The server treats a non-empty value
   * as spam and answers exactly like success, so this must be sent even when blank.
   */
  website?: string;
};

const trimmed = (v: string | undefined): string | undefined => {
  const s = v?.trim();
  return s ? s : undefined;
};

/**
 * Submit a business's application to become a recommended meetup venue.
 *
 * The venue is created in `pending` and is invisible to app users until someone approves it in
 * the ops console, so this endpoint never publishes anything on its own.
 */
export async function submitPartnerVenueApplication(
  application: PartnerVenueApplication
): Promise<{ ok: true } | { ok: false; message: string }> {
  const endpoint = marketingBackendApiUrl('/api/partner-venues/apply');
  if (!endpoint) {
    return {
      ok: false,
      message: 'Applications are not configured (set VITE_MARKETING_BACKEND_URL).',
    };
  }

  const name = application.name.trim();
  const city = application.city.trim();
  const contactEmail = application.contactEmail.trim();
  if (!name || !city) {
    return { ok: false, message: 'Business name and city are required.' };
  }
  if (!contactEmail.includes('@')) {
    return { ok: false, message: 'Please give us an email we can reply to.' };
  }
  // Required by the form, not by the server: an application with no categories would be accepted
  // and then never match anyone, which looks identical to being ignored.
  if (!application.categories?.length) {
    return { ok: false, message: 'Pick at least one kind of place so we know who to show you to.' };
  }

  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        city,
        contactEmail,
        state: trimmed(application.state),
        categories: application.categories?.length ? application.categories : undefined,
        address: trimmed(application.address),
        websiteUrl: trimmed(application.websiteUrl),
        contactName: trimmed(application.contactName),
        contactPhone: trimmed(application.contactPhone),
        description: trimmed(application.description),
        applicantNote: trimmed(application.applicantNote),
        offer: trimmed(application.offer),
        promoCode: trimmed(application.promoCode),
        // Sent as-is, including empty: the server distinguishes "absent" from "filled in".
        website: application.website ?? '',
      }),
    });
  } catch {
    return { ok: false, message: WAITLIST_UNAVAILABLE_MESSAGE };
  }

  // 429 is the rate limiter, and its body is not the shape the JSON parser expects. Say the useful
  // thing rather than the generic failure.
  if (res.status === 429) {
    return {
      ok: false,
      message: "That's a few applications in a short time. Try again a bit later, or email us.",
    };
  }

  const raw = await res.text();
  const parsed = parseMarketingApiJsonBody(raw, WAITLIST_UNAVAILABLE_MESSAGE);
  if (!parsed.ok) {
    return { ok: false, message: parsed.userMessage };
  }
  if (!res.ok) {
    const body = parsed.json as { error?: string } | undefined;
    return { ok: false, message: body?.error?.trim() || WAITLIST_UNAVAILABLE_MESSAGE };
  }
  return { ok: true };
}
